import React from 'react'
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    useMediaQuery,
    useTheme,
    Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import './editor/themes/PlaygroundEditorTheme.css'
import './editor/index.css'
import './editor/themes/blogs-content.css'
import { parseHtmlSanitizeAddTargetToLinks } from '../services/utils'
import useFindScopeLayer from '../hooks/useFindScopeLayer'

const themeColors = {
    primary: '#232536',
    hover: '#3a3e5a',
}

const buttonBorderRadius = '28px'

const PreviewBlogModal = ({
    open,
    onClose,
    title = '',
    category = '',
    author = '',
    content = '',
    showSnackbar,
    slug = '/blogs/preview',
}) => {
    const theme = useTheme()
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const isMobileDialog = useMediaQuery('(max-width:768px)')
    const isTabletDialog = useMediaQuery('(min-width:769px) and (max-width:960px)')

    // Scope browser Ctrl+F to this preview dialog's content while it is open.
    useFindScopeLayer(open, 'card-modal')

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: isMobileDialog ? '95vw' : isTabletDialog ? '90vw' : '85vw',
                    maxWidth: isMobileDialog ? '95vw' : isTabletDialog ? '90vw' : '85vw',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    m: 0,
                }
            }}
        >
            <DialogTitle
                sx={{
                    color: themeColors.primary,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box component='span'>Blog Preview</Box>
                {isMobileScreen ? (
                    <IconButton
                        onClick={onClose}
                        sx={{
                            backgroundColor: 'white',
                            border: '1px solid #232536',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                            flexShrink: 0,
                        }}
                        aria-label='close blog preview'
                        size='small'
                    >
                        <CloseIcon sx={{ color: '#232536' }} fontSize='small' />
                    </IconButton>
                ) : (
                    <Box
                        sx={{
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            border: '1px solid #232536',
                            borderRadius: buttonBorderRadius,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                            padding: '4px 12px',
                            flexShrink: 0,
                        }}
                        onClick={onClose}
                        role='button'
                        aria-label='close blog preview'
                    >
                        <CloseIcon
                            sx={{ color: themeColors.primary }}
                            fontSize='small'
                        />
                        <Typography
                            variant='button'
                            sx={{
                                color: themeColors.primary,
                                marginLeft: 0.5,
                                fontSize: '0.80rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Close
                        </Typography>
                    </Box>
                )}
            </DialogTitle>
            <DialogContent dividers data-find-scope='card-modal' sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', px: 3, pt: 2, pb: 0 }}>
                    <Typography variant='h5' sx={{ fontWeight: 700, mb: 1, flexShrink: 0 }}>
                        {title || 'Untitled'}
                    </Typography>
                    {(category || author) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, flexShrink: 0 }}>
                            {category && <Chip size='small' label={category} />}
                            {author && <Chip size='small' label={author} />}
                        </Box>
                    )}
                    <Box
                        className='blog-content blog-content--detail'
                        sx={{
                            border: '1px solid #e2e8f0',
                            borderRadius: 2,
                            p: 2,
                            pb: 4,
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            mb: 2,
                            '& img': { maxWidth: '100%', height: 'auto', display: 'block' },
                            '& pre': { whiteSpace: 'pre-wrap', overflowX: 'auto' },
                        }}
                    >
                        {parseHtmlSanitizeAddTargetToLinks(content, null, slug, {
                            fullDocumentContent: content,
                            documentTitle: title || 'Blog Preview',
                            showSnackbar,
                        })}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default PreviewBlogModal
